"use client";

import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import { Script } from "@packages/framework";
import { getName, useRequestMe } from "@packages/sdk";
import { createContext, useContext, useEffect, useRef, useState } from "react";

import { Loader } from "../other/Loader/Loader";

declare global {
  interface Window {
    checkoutElements: {
      init: (type: string, options: any) => { mount: (id: string) => void };
    };
  }
}

type HotmartElementProps = {
  offerCode: string;
  promoCode?: string;
};

export const hotmartContext = createContext<{ onError: () => void }>({
  onError: () => {},
});

export const HotmartElement = ({
  offerCode,
  promoCode,
}: HotmartElementProps) => {
  const { track } = useAnalytics();
  const { onError } = useContext(hotmartContext);
  const { query: me } = useRequestMe();
  const [scriptLoaded, setScriptLoaded] = useState<boolean>(false);
  const [mounted, setMounted] = useState<boolean>(false);
  const timeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (mounted) {
      clearTimeout(timeout.current);
      track({
        event: HallowAnalyticsEvent.ViewedScreen,
        properties: {
          screen_name: "hotmart_checkout",
        },
      });
    } else {
      clearTimeout(timeout.current);
      timeout.current = setTimeout(onError, 5000);
    }
  }, [mounted]);

  useEffect(() => {
    if (!!window.checkoutElements) {
      setScriptLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (
      scriptLoaded &&
      !!me.data &&
      typeof window !== "undefined" &&
      !!offerCode
    ) {
      try {
        const checkout = window.checkoutElements.init("inlineCheckout", {
          offer: offerCode,
          prefilledInfo: {
            name: getName({ user: me.data }),
            email: me.data.email ?? "",
            offDiscount: promoCode ?? "",
            xcod: me.data.hashed_id ?? "",
          },
          visibilityOptions: {
            hidePayPal: "1",
          },
        });
        checkout.mount("#hotmartCheckout");
        setMounted(true);
      } catch {
        onError();
      }
    }

    return () => {
      if (typeof document !== "undefined") {
        const checkoutEl = document.getElementById("hotmartCheckout");
        if (checkoutEl) checkoutEl.innerHTML = "";
      }
    };
  }, [me.data, scriptLoaded, offerCode, promoCode]);

  const handleHotmartLoad = () => {
    setScriptLoaded(true);
  };

  return (
    <>
      <Script
        src={"https://checkout.hotmart.com/lib/hotmart-checkout-elements.js"}
        onLoad={handleHotmartLoad}
      />
      {!mounted && <Loader />}
      <div id="hotmartCheckout" />
    </>
  );
};
